import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  ADOPTION_MAX_LIMIT,
  ADOPTION_PAGE_SIZE,
  ComparisonFilters,
  ComparisonLineItem,
  ComparisonRow,
  ComparisonWidget,
  GroupByDimension,
  ProductCustomerAdoptionResult,
  comparison_filters_without_product,
  comparisonDashboardService,
} from '../../services/comparisonDashboardService';
import {
  breakdown_item_number,
  comparison_secondary_dimension,
  display_group_label,
  drill_first_column_label,
  group_list_rows,
  ROLLUP_HIDDEN_METRIC_KEYS,
  rows_for_group,
  widget_for_key,
} from '../../utils/comparisonBreakdownDrilldown';
import {
  adoption_customers_csv,
  breakdown_composition_csv,
  breakdown_list_csv,
  download_csv,
  line_items_csv,
} from '../../utils/comparisonBreakdownExport';
import {
  COMPARISON_GROUP_BY_OPTIONS,
  COMPARISON_METRIC_COLUMNS,
  COMPARISON_SECONDARY_LABEL,
  CUSTOMER_ACTIVITY_COLUMNS,
  customer_activity_column_title,
  formatComparisonMetricValue,
} from './comparisonBreakdownConstants';
import ExportButton from './ExportButton';

const styles: Record<string, React.CSSProperties> = {
  groupByRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0 0 0.75rem',
    flexWrap: 'wrap' as const,
  },
  groupByLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: 'var(--report-text)',
  },
  groupByButtons: {
    display: 'flex',
    gap: '0.35rem',
    flexWrap: 'wrap' as const,
  },
  groupByBtn: {
    padding: '0.4rem 0.85rem',
    border: '1px solid var(--report-border)',
    borderRadius: '6px',
    background: 'var(--report-surface)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--report-text)',
    transition: 'all 0.15s',
  },
  groupByBtnActive: {
    background: 'var(--report-accent)',
    color: 'white',
  },
  loadingBox: {
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--report-text-muted)',
    fontSize: '0.95rem',
  },
  truncatedNotice: {
    marginBottom: '0.75rem',
    padding: '0.65rem 0.85rem',
    background: 'var(--report-surface-subtle)',
    border: '1px solid var(--report-border)',
    borderRadius: '6px',
    color: 'var(--report-text-muted)',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  loadMoreBtn: {
    padding: '0.35rem 0.75rem',
    border: '1px solid var(--report-border)',
    borderRadius: '6px',
    background: 'var(--report-surface)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--report-text)',
    whiteSpace: 'nowrap' as const,
  },
  widgetCard: {
    background: 'var(--report-surface)',
    border: '1px solid var(--report-border)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  widgetHeader: {
    padding: '0.75rem 1rem',
    fontWeight: 600,
    fontSize: '1.05rem',
    color: 'var(--report-text)',
    borderBottom: '1px solid var(--report-border)',
    background: 'var(--report-surface-subtle)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    padding: '0.5rem 0.65rem',
    textAlign: 'right' as const,
    fontWeight: 600,
    color: 'var(--report-text-muted)',
    borderBottom: '2px solid var(--report-border)',
    whiteSpace: 'nowrap' as const,
    fontSize: '0.8rem',
  },
  thFirst: {
    textAlign: 'left' as const,
  },
  td: {
    padding: '0.5rem 0.65rem',
    borderBottom: '1px solid var(--report-border)',
    color: 'var(--report-text)',
  },
  tdNum: {
    padding: '0.5rem 0.65rem',
    borderBottom: '1px solid var(--report-border)',
    color: 'var(--report-text)',
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  breadcrumbLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--report-accent)',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  chevron: {
    marginLeft: '0.5rem',
    fontSize: '0.75rem',
    color: 'var(--report-accent)',
  },
  hint: {
    padding: '0.5rem 1rem',
    fontSize: '0.8rem',
    color: 'var(--report-text-muted)',
  },
  itemNum: {
    fontVariantNumeric: 'tabular-nums' as const,
    color: 'var(--report-text-muted)',
    fontWeight: 400,
  },
  errorBox: {
    margin: '0.75rem 1rem',
    padding: '0.75rem 1rem',
    backgroundColor: 'var(--report-error-bg)',
    color: 'var(--report-error-text)',
    borderRadius: '6px',
    border: '1px solid var(--report-error-border)',
    fontSize: '0.9rem',
  },
  tabRow: {
    display: 'flex',
    gap: '0.35rem',
    padding: '0 1rem 0.5rem',
    flexWrap: 'wrap' as const,
  },
};

const ComparisonMetricTable: React.FC<{
  item_dimension: GroupByDimension;
  name_column_label: string;
  rows: ComparisonRow[];
  on_row_click?: (row: ComparisonRow) => void;
  hide_average_columns?: boolean;
  hide_metric_columns?: boolean;
  show_customer_activity?: boolean;
  activity_filters?: ComparisonFilters;
}> = ({
  item_dimension,
  name_column_label,
  rows,
  on_row_click,
  hide_average_columns,
  hide_metric_columns,
  show_customer_activity,
  activity_filters,
}) => {
  const metric_columns = hide_metric_columns
    ? []
    : hide_average_columns
      ? COMPARISON_METRIC_COLUMNS.filter(
          (col) =>
            !ROLLUP_HIDDEN_METRIC_KEYS.includes(
              col.key as (typeof ROLLUP_HIDDEN_METRIC_KEYS)[number]
            )
        )
      : COMPARISON_METRIC_COLUMNS;
  const columns = show_customer_activity
    ? [...CUSTOMER_ACTIVITY_COLUMNS, ...metric_columns]
    : metric_columns;

  return (
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, ...styles.thFirst }}>Item #</th>
            <th style={{ ...styles.th, ...styles.thFirst }}>{name_column_label}</th>
            {columns.map((col) => (
              <th
                key={col.key}
                style={styles.th}
                title={customer_activity_column_title(col.key, activity_filters)}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.row_key}::${row.row_label}::${index}`}
              onClick={on_row_click ? () => on_row_click(row) : undefined}
              style={on_row_click ? { cursor: 'pointer' } : undefined}
              onMouseEnter={
                on_row_click
                  ? (e) => {
                      e.currentTarget.style.backgroundColor = 'var(--report-row-hover)';
                    }
                  : undefined
              }
              onMouseLeave={
                on_row_click
                  ? (e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  : undefined
              }
            >
              <td style={{ ...styles.td, ...styles.itemNum }}>
                {breakdown_item_number(item_dimension, row.row_key) || '—'}
              </td>
              <td style={{ ...styles.td, fontWeight: 500 }}>
                {display_group_label(row.row_key, row.row_label)}
                {on_row_click && <span style={styles.chevron}>▶</span>}
              </td>
              {columns.map((col) => (
                <td key={col.key} style={styles.tdNum}>
                  {formatComparisonMetricValue(
                    (row as unknown as Record<string, number>)[col.key],
                    col.format
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LineItemsTable: React.FC<{ rows: ComparisonLineItem[] }> = ({ rows }) => (
  <div style={styles.tableWrapper}>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={{ ...styles.th, ...styles.thFirst }}>Date</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Invoice #</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Customer #</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Customer</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Item #</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Name</th>
          <th style={styles.th}>Qty</th>
          <th style={styles.th}>Revenue</th>
          <th style={styles.th}>Unit Price</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${row.invoice_number}-${row.item_number}-${index}`}>
            <td style={styles.td}>{row.date}</td>
            <td style={styles.td}>{row.invoice_number}</td>
            <td style={{ ...styles.td, ...styles.itemNum }}>{row.customer_number}</td>
            <td style={styles.td}>{row.customer_name}</td>
            <td style={{ ...styles.td, ...styles.itemNum }}>{row.item_number}</td>
            <td style={styles.td}>{row.item_name}</td>
            <td style={styles.tdNum}>{formatComparisonMetricValue(row.qty, 'number')}</td>
            <td style={styles.tdNum}>{formatComparisonMetricValue(row.revenue, 'currency')}</td>
            <td style={styles.tdNum}>{formatComparisonMetricValue(row.unit_price, 'currency')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function export_filename(prefix: string, filters?: ComparisonFilters): string {
  const start = filters?.start_date || 'start';
  const end = filters?.end_date || 'end';
  return `${prefix}-${start}-to-${end}.csv`;
}

function is_product_adoption_view(group_by: GroupByDimension, drilled_key: string | null): boolean {
  return group_by === 'product' && Boolean(drilled_key && drilled_key.trim());
}

function product_adoption_empty_message(
  status: 'bought' | 'not_bought',
  bought_count: number,
  not_bought_count: number
): string {
  if (bought_count + not_bought_count === 0) {
    return 'No customers in this date range.';
  }
  if (status === 'bought') {
    return 'No customers bought this product in the date range.';
  }
  return 'Every customer in this date range bought this product.';
}

type ProductAdoptionTabStatus = 'bought' | 'not_bought';

interface ProductAdoptionState {
  status: ProductAdoptionTabStatus;
  rows: ComparisonRow[];
  has_more: boolean;
  bought_count: number;
  not_bought_count: number;
  counts_known: boolean;
  loading: boolean;
  loading_more: boolean;
  error: string | null;
  load_more_error: string | null;
}

type ProductAdoptionAction =
  | { type: 'reset'; status: ProductAdoptionTabStatus; keep_counts?: boolean }
  | { type: 'load_started'; append: boolean }
  | { type: 'load_succeeded'; append: boolean; data: ProductCustomerAdoptionResult }
  | { type: 'load_failed'; append: boolean; message: string };

export function adoption_tab_label(name: string, count: number, counts_known: boolean): string {
  if (!counts_known) return name;
  return `${name} (${count})`;
}

/** Same product and filters share one key, so a new object with the same values does not refetch. */
export function adoption_scope_key(product_key: string, filters: ComparisonFilters): string {
  return [
    product_key,
    filters.start_date ?? '',
    filters.end_date ?? '',
    (filters.brands ?? []).join(','),
    (filters.categories ?? []).join(','),
    (filters.products ?? []).join(','),
    (filters.product_ids ?? []).join(','),
    (filters.customers ?? []).join(','),
    (filters.sales_reps ?? []).join(','),
    (filters.territories ?? []).join(','),
  ].join('|');
}

export function adoption_state_from_result(
  data: ProductCustomerAdoptionResult
): ProductAdoptionState {
  const status: ProductAdoptionTabStatus = data.status === 'not_bought' ? 'not_bought' : 'bought';
  return {
    status,
    rows: data.rows,
    has_more: data.has_more,
    bought_count: data.bought_count,
    not_bought_count: data.not_bought_count,
    counts_known: true,
    loading: false,
    loading_more: false,
    error: null,
    load_more_error: null,
  };
}

export function initial_product_adoption_state(
  status: ProductAdoptionTabStatus = 'bought'
): ProductAdoptionState {
  return {
    status,
    rows: [],
    has_more: false,
    bought_count: 0,
    not_bought_count: 0,
    counts_known: false,
    loading: true,
    loading_more: false,
    error: null,
    load_more_error: null,
  };
}

export function product_adoption_reducer(
  state: ProductAdoptionState,
  action: ProductAdoptionAction
): ProductAdoptionState {
  switch (action.type) {
    case 'reset': {
      const next = initial_product_adoption_state(action.status);
      if (!action.keep_counts) return next;
      return {
        ...next,
        bought_count: state.bought_count,
        not_bought_count: state.not_bought_count,
        counts_known: state.counts_known,
      };
    }
    case 'load_started':
      return {
        ...state,
        loading: !action.append,
        loading_more: action.append,
        error: action.append ? state.error : null,
        load_more_error: null,
      };
    case 'load_succeeded':
      return {
        ...state,
        rows: action.append ? [...state.rows, ...action.data.rows] : action.data.rows,
        has_more: action.data.has_more,
        bought_count: action.data.bought_count,
        not_bought_count: action.data.not_bought_count,
        counts_known: true,
        loading: false,
        loading_more: false,
        error: null,
        load_more_error: null,
      };
    case 'load_failed':
      return {
        ...state,
        rows: action.append ? state.rows : [],
        has_more: action.append ? state.has_more : false,
        loading: false,
        loading_more: false,
        error: action.append ? state.error : action.message,
        load_more_error: action.append ? action.message : null,
      };
  }
}

const ProductAdoptionLists: React.FC<{
  product_key: string;
  comparisonFilters: ComparisonFilters;
  initial_result: ProductCustomerAdoptionResult;
  on_row_click: (row: ComparisonRow, status: ProductAdoptionTabStatus) => void;
}> = ({ product_key, comparisonFilters, initial_result, on_row_click }) => {
  const [adoption_state, dispatch_adoption] = useReducer(
    product_adoption_reducer,
    initial_result,
    adoption_state_from_result
  );
  const {
    status,
    rows,
    has_more,
    bought_count,
    not_bought_count,
    counts_known,
    loading,
    loading_more,
    error,
    load_more_error,
  } = adoption_state;
  const request_id = useRef(0);
  const scope_key = adoption_scope_key(product_key, comparisonFilters);
  const loaded_scope = useRef<string | null>(scope_key);

  const load = useCallback(
    async (next_status: 'bought' | 'not_bought', append: boolean, last?: ComparisonRow) => {
      const id = ++request_id.current;
      dispatch_adoption({ type: 'load_started', append });
      try {
        const data = await comparisonDashboardService.getProductCustomers(
          comparisonFilters,
          product_key,
          next_status,
          ADOPTION_PAGE_SIZE,
          last
            ? {
                after_key: last.row_key,
                after_label: last.row_label,
                after_revenue: last.revenue,
              }
            : undefined
        );
        if (id !== request_id.current) return;
        dispatch_adoption({ type: 'load_succeeded', append, data });
      } catch (err: unknown) {
        if (id !== request_id.current) return;
        const message = err instanceof Error ? err.message : 'Failed to load customers';
        dispatch_adoption({ type: 'load_failed', append, message });
      }
    },
    [comparisonFilters, product_key]
  );

  useEffect(() => {
    if (loaded_scope.current === scope_key) return;
    loaded_scope.current = scope_key;
    dispatch_adoption({ type: 'reset', status: 'bought' });
    void load('bought', false);
  }, [load, scope_key]);

  const handle_tab = (next: 'bought' | 'not_bought') => {
    if (next === status) return;
    dispatch_adoption({ type: 'reset', status: next, keep_counts: true });
    void load(next, false);
  };

  return (
    <>
      <div style={styles.tabRow}>
        <button
          type="button"
          onClick={() => handle_tab('bought')}
          style={{
            ...styles.groupByBtn,
            ...(status === 'bought' ? styles.groupByBtnActive : {}),
          }}
        >
          {adoption_tab_label('Bought', bought_count, counts_known)}
        </button>
        <button
          type="button"
          onClick={() => handle_tab('not_bought')}
          style={{
            ...styles.groupByBtn,
            ...(status === 'not_bought' ? styles.groupByBtnActive : {}),
          }}
        >
          {adoption_tab_label('Did not buy', not_bought_count, counts_known)}
        </button>
      </div>
      {loading ? (
        <div style={styles.loadingBox}>Loading customers...</div>
      ) : error ? (
        <div style={styles.errorBox}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={styles.hint}>
          {product_adoption_empty_message(status, bought_count, not_bought_count)}
        </div>
      ) : (
        <>
          <ComparisonMetricTable
            item_dimension="customer"
            name_column_label="Customer"
            rows={rows}
            on_row_click={(row) => on_row_click(row, status)}
            hide_metric_columns={status === 'not_bought'}
            show_customer_activity={status === 'not_bought'}
            activity_filters={comparisonFilters}
          />
          {load_more_error && (
            <div style={styles.errorBox}>
              Could not load more customers. The customers already shown did not change.
            </div>
          )}
          {has_more && (
            <div style={styles.truncatedNotice}>
              <span>
                Showing {rows.length} of {status === 'bought' ? bought_count : not_bought_count}{' '}
                customers.
              </span>
              <button
                type="button"
                style={styles.loadMoreBtn}
                onClick={() => load(status, true, rows[rows.length - 1])}
                disabled={loading_more}
              >
                {loading_more ? 'Loading…' : load_more_error ? 'Retry' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

const DimensionDrilldownCard: React.FC<{
  groupBy: GroupByDimension;
  widgets: ComparisonWidget[];
  comparisonFilters?: ComparisonFilters;
}> = ({ groupBy, widgets, comparisonFilters }) => {
  const [drilled_key, set_drilled_key] = useState<string | null>(null);
  const [prepared_adoption, set_prepared_adoption] = useState<ProductCustomerAdoptionResult | null>(
    null
  );
  const [opening_product, set_opening_product] = useState<ComparisonRow | null>(null);
  const [open_error, set_open_error] = useState<string | null>(null);
  const open_request_id = useRef(0);
  const [line_item_row, set_line_item_row] = useState<ComparisonRow | null>(null);
  const [customer_invoices, set_customer_invoices] = useState(false);
  const [line_items, set_line_items] = useState<ComparisonLineItem[]>([]);
  const [line_items_loading, set_line_items_loading] = useState(false);
  const [line_items_error, set_line_items_error] = useState<string | null>(null);
  const [line_items_truncated, set_line_items_truncated] = useState(false);
  const [adoption_exporting, set_adoption_exporting] = useState(false);
  const [adoption_export_error, set_adoption_export_error] = useState<string | null>(null);
  const adoption_export_request_id = useRef(0);

  const group_label =
    COMPARISON_GROUP_BY_OPTIONS.find((opt) => opt.value === groupBy)?.label ?? 'Group';
  const secondary_label = COMPARISON_SECONDARY_LABEL[groupBy];

  useEffect(() => {
    if (drilled_key && !widgets.some((widget) => widget.group_key === drilled_key)) {
      set_drilled_key(null);
      set_prepared_adoption(null);
      set_opening_product(null);
      set_line_item_row(null);
      set_customer_invoices(false);
    }
  }, [widgets, drilled_key]);

  useEffect(() => {
    set_adoption_export_error(null);
    set_adoption_exporting(false);
    return () => {
      adoption_export_request_id.current += 1;
    };
  }, [groupBy, drilled_key, comparisonFilters]);

  useEffect(() => {
    if (!line_item_row || !comparisonFilters || !drilled_key) {
      set_line_items([]);
      set_line_items_truncated(false);
      set_line_items_error(null);
      return;
    }

    let cancelled = false;
    set_line_items_loading(true);
    set_line_items_error(null);

    void (async () => {
      try {
        const data = customer_invoices
          ? await comparisonDashboardService.getLineItems(
              comparison_filters_without_product(comparisonFilters),
              'customer',
              line_item_row.row_key
            )
          : await comparisonDashboardService.getLineItems(
              comparisonFilters,
              groupBy,
              drilled_key,
              line_item_row.row_key
            );
        if (cancelled) return;
        set_line_items(data.rows);
        set_line_items_truncated(data.truncated);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load invoice lines';
        set_line_items_error(message);
        set_line_items([]);
      } finally {
        if (!cancelled) set_line_items_loading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [line_item_row, comparisonFilters, drilled_key, groupBy, customer_invoices]);

  const drilled_widget = widget_for_key(widgets, drilled_key);
  const is_product_adoption = is_product_adoption_view(groupBy, drilled_key);
  const rows = drilled_widget
    ? rows_for_group(widgets, drilled_widget.group_key)
    : group_list_rows(widgets);
  const name_column_label = drill_first_column_label(drilled_key, group_label, secondary_label);

  const close_drilldown = () => {
    open_request_id.current += 1;
    set_opening_product(null);
    set_open_error(null);
    set_prepared_adoption(null);
    set_line_item_row(null);
    set_customer_invoices(false);
    set_drilled_key(null);
  };

  const open_product = async (row: ComparisonRow) => {
    if (!comparisonFilters) return;
    if (opening_product?.row_key === row.row_key) return;
    const id = ++open_request_id.current;
    set_open_error(null);
    set_opening_product(row);
    try {
      const data = await comparisonDashboardService.getProductCustomers(
        comparisonFilters,
        row.row_key,
        'bought',
        ADOPTION_PAGE_SIZE
      );
      if (id !== open_request_id.current) return;
      set_prepared_adoption(data);
      set_drilled_key(row.row_key);
      set_opening_product(null);
    } catch (err: unknown) {
      if (id !== open_request_id.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load customers';
      set_open_error(message);
      set_opening_product(null);
    }
  };

  const handle_row_click = (row: ComparisonRow) => {
    if (!drilled_widget) {
      if (groupBy === 'product') {
        void open_product(row);
        return;
      }
      set_drilled_key(row.row_key);
      return;
    }
    if (comparisonFilters) {
      set_line_item_row(row);
    }
  };

  const handle_download = () => {
    if (line_item_row) {
      download_csv(
        export_filename(`breakdown-lines-${line_item_row.row_key}`, comparisonFilters),
        line_items_csv(line_items, line_items_truncated)
      );
      return;
    }
    if (is_product_adoption && drilled_key && comparisonFilters) {
      const export_request_id = ++adoption_export_request_id.current;
      set_adoption_exporting(true);
      set_adoption_export_error(null);
      void (async () => {
        try {
          const data = await comparisonDashboardService.getProductCustomers(
            comparisonFilters,
            drilled_key,
            'both',
            ADOPTION_MAX_LIMIT
          );
          if (export_request_id !== adoption_export_request_id.current) return;
          download_csv(
            export_filename(`breakdown-product-${drilled_key}-customers`, comparisonFilters),
            adoption_customers_csv(data.bought_rows, data.not_bought_rows, data.truncated)
          );
        } catch (err: unknown) {
          if (export_request_id !== adoption_export_request_id.current) return;
          const message = err instanceof Error ? err.message : 'Failed to export customers';
          set_adoption_export_error(message);
        } finally {
          if (export_request_id === adoption_export_request_id.current) {
            set_adoption_exporting(false);
          }
        }
      })();
      return;
    }
    if (drilled_widget) {
      download_csv(
        export_filename(`breakdown-${groupBy}-${drilled_widget.group_key}`, comparisonFilters),
        breakdown_list_csv(rows, comparison_secondary_dimension(groupBy))
      );
      return;
    }
    download_csv(
      export_filename(`breakdown-${groupBy}`, comparisonFilters),
      breakdown_composition_csv(widgets, groupBy)
    );
  };

  const header_label = line_item_row
    ? `${line_item_row.row_key} ${line_item_row.row_label}`
    : drilled_widget
      ? drilled_widget.group_label
      : group_label;

  const hint = line_item_row
    ? customer_invoices
      ? 'Invoices for this customer in the date range. Each row is an invoice line.'
      : 'Invoice lines that make up this total.'
    : is_product_adoption
      ? 'Bought = net quantity of this product > 0 in the date range (returns net out). Did not buy = other customers who invoiced in this date range, not the full customer master. Total Sales and Orders cover all products in the date range. Click a customer to see invoice lines.'
      : drilled_widget
        ? 'Click a row to see the invoice lines behind that number.'
        : groupBy === 'product'
          ? 'Click a product to see who bought it and who did not.'
          : 'Click a row to see what makes up that number.';

  const export_disabled = line_item_row
    ? line_items_loading || line_items.length === 0
    : is_product_adoption
      ? adoption_exporting || !comparisonFilters
      : rows.length === 0;

  return (
    <div>
      {(drilled_widget || line_item_row) && (
        <div style={styles.breadcrumb}>
          <button type="button" style={styles.breadcrumbLink} onClick={close_drilldown}>
            {group_label}
          </button>
          {drilled_widget && (
            <>
              <span style={{ color: 'var(--report-text-muted)' }}>›</span>
              {line_item_row ? (
                <button
                  type="button"
                  style={styles.breadcrumbLink}
                  onClick={() => {
                    set_line_item_row(null);
                    set_customer_invoices(false);
                  }}
                >
                  {drilled_widget.group_label}
                </button>
              ) : (
                <span style={{ color: 'var(--report-text-muted)' }}>
                  {drilled_widget.group_label}
                </span>
              )}
            </>
          )}
          {line_item_row && (
            <>
              <span style={{ color: 'var(--report-text-muted)' }}>›</span>
              <span style={{ color: 'var(--report-text-muted)' }}>
                {line_item_row.row_key} {line_item_row.row_label}
              </span>
            </>
          )}
        </div>
      )}
      <div style={styles.widgetCard}>
        <div style={styles.widgetHeader}>
          <span>{header_label}</span>
          <ExportButton
            onClick={handle_download}
            disabled={export_disabled}
            loading={adoption_exporting}
            size="small"
            title={
              is_product_adoption && !line_item_row
                ? 'Download bought and did-not-buy lists (CSV)'
                : 'Download this list (CSV)'
            }
          />
        </div>
        <div style={styles.hint}>{hint}</div>
        {adoption_export_error && !line_item_row && (
          <div style={styles.errorBox}>{adoption_export_error}</div>
        )}
        {line_item_row ? (
          line_items_loading ? (
            <div style={styles.loadingBox}>Loading invoice lines...</div>
          ) : line_items_error ? (
            <div style={styles.errorBox}>{line_items_error}</div>
          ) : line_items.length === 0 ? (
            <div style={styles.hint}>
              {customer_invoices
                ? 'No invoice lines for this customer in the date range.'
                : 'No invoice lines for this item.'}
            </div>
          ) : (
            <>
              {line_items_truncated && (
                <div style={styles.hint}>Showing the first 500 invoice lines.</div>
              )}
              <LineItemsTable rows={line_items} />
            </>
          )
        ) : is_product_adoption && drilled_key && comparisonFilters && prepared_adoption ? (
          <ProductAdoptionLists
            key={drilled_key}
            product_key={drilled_key}
            comparisonFilters={comparisonFilters}
            initial_result={prepared_adoption}
            on_row_click={(row, status) => {
              set_customer_invoices(status === 'not_bought');
              set_line_item_row(row);
            }}
          />
        ) : (
          <>
            {opening_product && (
              <div style={styles.loadingBox}>
                Loading customers for {opening_product.row_label || opening_product.row_key}…
              </div>
            )}
            {open_error && <div style={styles.errorBox}>{open_error}</div>}
            <ComparisonMetricTable
              item_dimension={drilled_widget ? comparison_secondary_dimension(groupBy) : groupBy}
              name_column_label={name_column_label}
              rows={rows}
              on_row_click={handle_row_click}
              hide_average_columns={!drilled_widget}
            />
          </>
        )}
      </div>
    </div>
  );
};

export interface ComparisonBreakdownGridProps {
  groupBy: GroupByDimension;
  onGroupByChange: (gb: GroupByDimension) => void;
  widgets: ComparisonWidget[];
  loading: boolean;
  emptyMessage?: string;
  comparisonFilters?: ComparisonFilters;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

/**
 * Group-by toolbar and one drilldown table (group list, child rows, then invoice lines).
 */
const ComparisonBreakdownGrid: React.FC<ComparisonBreakdownGridProps> = ({
  groupBy,
  onGroupByChange,
  widgets,
  loading,
  emptyMessage = 'No data found for the selected filters.',
  comparisonFilters,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
}) => (
  <>
    <div style={styles.groupByRow}>
      <span style={styles.groupByLabel}>Group by</span>
      <div style={styles.groupByButtons}>
        {COMPARISON_GROUP_BY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onGroupByChange(opt.value)}
            style={{
              ...styles.groupByBtn,
              ...(groupBy === opt.value ? styles.groupByBtnActive : {}),
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>

    {loading ? (
      <div style={styles.loadingBox}>Loading comparison data...</div>
    ) : widgets.length === 0 ? (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--report-text-muted)' }}>
        {emptyMessage}
      </div>
    ) : (
      <>
        {hasMore && (
          <div style={styles.truncatedNotice}>
            <span>
              Showing {widgets.length} groups by revenue. Add a filter to narrow, or load more.
            </span>
            {onLoadMore && (
              <button
                type="button"
                style={styles.loadMoreBtn}
                onClick={onLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}
        <DimensionDrilldownCard
          key={groupBy}
          groupBy={groupBy}
          widgets={widgets}
          comparisonFilters={comparisonFilters}
        />
      </>
    )}
  </>
);

export default ComparisonBreakdownGrid;
